import {
  ComcastErrorType,
  getScaledResolution,
  platformTitle,
  stripHttpProtocol,
  triggerSentryError,
} from '../../../../shared/helpers'

import configuration from '../../configuration'
import type { ContentState } from '../../reducers/types/reducerTypes'
import type { AdTagParams } from './types'

function getDeviceTypeParameter(deviceModel: string): string {
  if (COMPILING_FOR_TIZEN) {
    return `OTTDevice%3DSamsungTV%26deviceID%3D${deviceModel}`
  } else if (COMPILING_FOR_VIZIO) {
    return `OTTDevice%3DVizioTV%26deviceID%3D${deviceModel}`
  } else if (COMPILING_FOR_COMCASTX1) {
    return `OTTDevice%3DComcastX1%26deviceID%3D${deviceModel}`
  } else if (COMPILING_FOR_LGTV) {
    return `OTTDevice%3DLGTV%26deviceID%3D${deviceModel}`
  }
  return 'OTTDevice%3DBrowser'
}

export function buildVideoAdTagUrl({
  contentScope,
  callSign,
  legacyTpMediaId,
  ppid,
  latEnabled,
  deviceModel,
  validPassport,
}: AdTagParams): string {
  const contentScopeValue = contentScope === 'national' ? 'National' : callSign
  const latParam = latEnabled ? '&is_lat=1' : ''
  const deviceType = getDeviceTypeParameter(deviceModel)

  return `https://pubads.g.doubleclick.net/gampad/ads?sz=400x300&ciu_szs=728x90&impl=s&gdfp_req=1&env=vp&cmsid=2588203&output=vmap&unviewed_position_start=1&ad_rule=1&iu=22540141786%2Fn6735.pbs%2FPBS_Video_${contentScopeValue}&vid=${legacyTpMediaId}&ppid=${ppid}${latParam}&cust_params=pbsuser%3Dyes%26station%3D${callSign.toLowerCase()}%26${deviceType}%26passport%3D${
    validPassport ? 'yes' : 'no'
  }&correlator=`
}

export function buildMezzanineUrl(assetMezzanine: string): string {
  const { width, height } = getScaledResolution()
  return `url("${stripHttpProtocol(
    assetMezzanine
  )}?crop=${width}x${height}&format=webp")`
}

export async function updateWatchHistory(
  videoId: string,
  secondsWatched: number
): Promise<void> {
  try {
    await updateWatchHistory(videoId, secondsWatched)
  } catch (error) {
    triggerSentryError('updateWatchHistory', 'VideoPlayer.tsx', {
      errorMessage: error.message ?? 'Undefined error message (default)',
      videoId: videoId,
      secondsWatched: 0,
      errorLocation: 'VideoPlayer - updateWatchHistory',
      errorType: ComcastErrorType.other,
    })
  }
}

export function buildMuxData(
  videoDetails: ContentState['videoDetails'],
  userPid: string,
  deviceModel: string,
  playerVersion: string
) {
  // https://docs.mux.com/guides/data/make-your-data-actionable-with-metadata
  const source_type = videoDetails.drmWidevideLicenseUrl
    ? 'application/dash+xml'
    : 'application/x-mpegurl'
  const playerType = videoDetails.drmWidevideLicenseUrl ? 'DRM' : 'NO_DRM'
  return {
    env_key: configuration?.advertising?.muxPropertyKey,
    page_type: '',
    viewer_user_id: userPid,
    experiment_name: playerType,
    sub_property_id: '',
    //For mux-embed
    player_software: playerType,
    player_software_version: playerVersion,
    player_mux_plugin_name: 'otterga-embed',
    player_mux_plugin_version: '0.0.1',
    // Player Metadata
    player_name: 'tizen-ga',
    player_version: '3.0.0', //This is our version, not player software version (e.g. Video.js 5.0.0). The latter is automatically tracked by the SDK.
    player_init_time: Date.now(),
    // Video Metadata (cleared with 'videochange' event)
    video_id: videoDetails.videoId,
    video_title: `${videoDetails.showTitle} | ${videoDetails.episodeTitle} | ${videoDetails.legacyTpMediaId} | ${videoDetails.contentType}`,
    video_stream_type: 'on-demand',
    source_type,
    video_series: videoDetails.parentShowSlug,
    video_duration: videoDetails.duration * 1000,
    viewer_device_name: platformTitle(),
    viewer_device_model: deviceModel,
    video_cdn: null,
    video_encoding_variant: 'x264,',
  }
}
